'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { trackSearch } from '@/lib/activityClient';
import { Map as MapIcon, List, Columns2, Loader2, ChevronLeft, ChevronRight, Search, SearchX, X, Link2, Check, MapPin, RotateCw } from 'lucide-react';
import type { BoundingBox, SearchResult } from '@/types/listing';
import { searchListings } from '@/lib/api';
import {
  type SearchFilters,
  type SearchView,
  citySlugsFromFilters,
  propertyTypesFromFilters,
  filtersToApiParams,
  filtersToQueryString,
  filtersToResultsKey,
  parseSearchParams,
  bboxFromFilters,
  bboxToParam,
  sqftLabel,
  rangeContradictions,
  searchPageTitle,
  DEFAULT_FILTERS,
} from '@/lib/searchParams';
import { feedGapForCities } from '@/lib/cityCoverage';
import { formatPrice, slugify, titleCaseCity, getPropertyTypeLabel } from '@/lib/utils';
import { recordSearch } from '@/lib/recentSearches';
import { loadListScroll, saveListScroll } from '@/lib/listScroll';
import { CONTACT_EMAIL_HREF, CONTACT_PHONE, CONTACT_PHONE_TEL } from '@/lib/contact';
import { useSearchStore } from '@/stores/searchStore';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import SearchBar from '@/components/search/SearchBar';
import FilterPanel from '@/components/search/FilterPanel';
import ListingCard from '@/components/search/ListingCard';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';

const ITEMS_PER_PAGE = 20;

/** Well-covered cities offered as discovery when a search comes up empty. */
const DISCOVERY_CITIES: { name: string; slug: string }[] = [
  { name: 'San Francisco', slug: 'san-francisco' },
  { name: 'San Jose', slug: 'san-jose' },
  { name: 'Oakland', slug: 'oakland' },
  { name: 'Palo Alto', slug: 'palo-alto' },
  { name: 'Berkeley', slug: 'berkeley' },
  { name: 'Sunnyvale', slug: 'sunnyvale' },
];

// Mapbox GL (~1.5 MB) loads only when a map pane is actually shown.
const SearchMap = dynamic(() => import('@/components/search/SearchMap'), {
  ssr: false,
  loading: () => (
    <div className="flex h-full w-full items-center justify-center bg-surface">
      <Loader2 className="h-7 w-7 animate-spin text-emerald-400" />
    </div>
  ),
});

interface SearchClientProps {
  initialFilters: SearchFilters;
  initialData: SearchResult | null;
}

export default function SearchClient({ initialFilters, initialData }: SearchClientProps) {
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  // The view lives in `filters` (i.e. in the URL) rather than in local state:
  // `?view=map` must actually select the map, survive the next filter change,
  // and come back with Back. It used to be a `useState('split')` no URL could
  // reach. (W13 R121)
  const viewMode = filters.view;
  const [mapTouched, setMapTouched] = useState(false);
  // Bounds the user panned to but hasn't applied yet ("Search this area").
  const [pendingBbox, setPendingBbox] = useState<BoundingBox | null>(null);
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const searchAsIMove = useSearchStore((s) => s.searchAsIMove);
  const setSearchAsIMove = useSearchStore((s) => s.setSearchAsIMove);

  const initialQs = useMemo(() => filtersToQueryString(initialFilters), [initialFilters]);
  // Stable: the map mounts once; a bbox arriving via URL sets its first view.
  const initialBounds = useMemo(() => bboxFromFilters(initialFilters), [initialFilters]);

  // Scrollable results pane — reset to top on a new search/page so page 2
  // doesn't open already scrolled to the bottom. Only applyFilters (forward
  // filter/sort/pagination) scrolls; popstate (Back) doesn't, so returning to
  // results keeps the prior scroll position.
  const listPanelRef = useRef<HTMLDivElement>(null);

  // URL is the source of truth: filter changes rewrite the query string
  // (shallow — no server round-trip), back/forward re-parse it.
  //
  // Every change used to `replaceState`, so there was nothing to go back *to*:
  // applying a price filter and then a beds filter and pressing Back left
  // `/homes` for the homepage and dropped the whole search (verified in the
  // browser, W12 R120) — even though the popstate handler below exists precisely
  // to undo a filter. Discrete choices now push an entry, so Back undoes the
  // last one. `replace: true` is for changes the user did not deliberately
  // make one at a time — a map pan under "search as I move" fires on every
  // `moveend` and would bury the history under dozens of entries.
  const lastWrittenQs = useRef(initialQs);
  const applyFilters = useCallback((next: SearchFilters, opts?: { replace?: boolean }) => {
    setFilters(next);
    const qs = filtersToQueryString(next);
    const url = `/homes${qs || ''}`;
    if (qs !== lastWrittenQs.current && !opts?.replace) {
      window.history.pushState(window.history.state, '', url);
    } else {
      window.history.replaceState(window.history.state, '', url);
    }
    lastWrittenQs.current = qs;
    listPanelRef.current?.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    const onPopState = () => {
      const params = Object.fromEntries(new URLSearchParams(window.location.search));
      const parsed = parseSearchParams(params);
      lastWrittenQs.current = filtersToQueryString(parsed);
      setFilters(parsed);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // External navigations (header SearchBar router.push while already on
  // /homes) re-render with new searchParams but used to leave this state
  // stale — the URL and tab title changed while the grid kept old results.
  // Adopt the external URL whenever it differs from what we last wrote.
  const routeParams = useSearchParams();
  useEffect(() => {
    const parsed = parseSearchParams(Object.fromEntries(routeParams));
    const externalQs = filtersToQueryString(parsed);
    if (externalQs !== lastWrittenQs.current) {
      lastWrittenQs.current = externalQs;
      setFilters(parsed);
    }
  }, [routeParams]);

  const updateFilters = useCallback(
    (partial: Partial<SearchFilters>, opts?: { replace?: boolean }) => {
      applyFilters({ ...filters, ...partial, page: partial.page ?? 0 }, opts);
    },
    [filters, applyFilters]
  );

  /**
   * Switch layout. A deliberate click, so it pushes a history entry (Back
   * returns to the previous view) — and unlike `updateFilters` it does NOT reset
   * `page`, because moving the map into view is not a new search.
   */
  const setViewMode = useCallback(
    (next: SearchView) => {
      if (next === filters.view) return;
      applyFilters({ ...filters, view: next });
    },
    [filters, applyFilters]
  );

  /**
   * Filters cleared, layout kept. Wiping back to `DEFAULT_FILTERS` would also
   * reset `view` to `split` now that it lives in the URL — so "Clear filters"
   * from the map-view empty state would have thrown the buyer out of the map
   * they were using. (W13 R121)
   */
  const clearedTo = useCallback(
    (over: Partial<SearchFilters> = {}): SearchFilters => ({
      ...DEFAULT_FILTERS,
      view: filters.view,
      ...over,
    }),
    [filters.view]
  );

  const resetFilters = useCallback(() => {
    applyFilters(clearedTo({ q: filters.q, cities: filters.cities }));
  }, [applyFilters, clearedTo, filters.q, filters.cities]);

  // URL identity (includes `view`) vs results identity (does not). Keying the
  // queries on the URL would refetch the same twenty homes every time the
  // layout toggle moved.
  const filtersQS = filtersToResultsKey(filters);
  const appliedBbox = useMemo(() => bboxFromFilters(filters), [filters]);
  const isInitialKey = filtersQS === filtersToResultsKey(initialFilters);

  // Report the search itself, keyed on the same string that identifies a distinct
  // result set — so paging or toggling the layout does not re-report, which would
  // let one search outweigh a week of browsing in the derivation.
  useEffect(() => {
    void trackSearch({
      // `cities` is a comma-separated URL param, not an array.
      cities: filters.cities ? filters.cities.split(',').filter(Boolean) : undefined,
      price_min: filters.priceMin,
      price_max: filters.priceMax,
      beds_min: filters.bedsMin,
      property_types: propertyTypesFromFilters(filters),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersQS]);

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: ['listings', filtersQS],
    queryFn: ({ signal }) =>
      searchListings({ ...filtersToApiParams(filters), bbox: appliedBbox }, { signal }),
    initialData: isInitialKey ? (initialData ?? undefined) : undefined,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  // The map pane mounts only when actually visible (mobile split hides it)
  const mapMounted = viewMode === 'map' || (viewMode === 'split' && isDesktop);

  // Separate light query fetches up to 500 matches so the map can cluster
  // the WHOLE result set, not just the visible page of 20.
  const { data: mapData } = useQuery({
    queryKey: ['map-listings', filtersQS],
    queryFn: ({ signal }) =>
      searchListings(
        { ...filtersToApiParams(filters, 500), bbox: appliedBbox, page: 0, light: true },
        { signal }
      ),
    enabled: mapMounted,
    staleTime: 60_000,
  });
  const mapListings = mapData?.listings ?? [];

  const listings = data?.listings ?? [];

  /**
   * Remember where the buyer was in the list, and put them back there.
   *
   * The browser restores document scroll across history entries; this list is an
   * inner `overflow-y-auto` pane, which no browser restores. Measured on the
   * deployed site: San Jose scrolled to `scrollTop: 2400` (the ninth of 24
   * cards), open a home, press Back → the pane returns at **0** with the first
   * card on top. With 1,935 matches, every home a buyer opens costs them their
   * place in the list. (W13 R125)
   *
   * Saved against the *results* key so a different search opens at the top and
   * the same search resumes; `view` is excluded, so flipping list ⇄ split keeps
   * the position too.
   */
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * The last position actually *observed* while the pane was on screen.
   *
   * This ref exists because reading `scrollTop` in the unmount cleanup does not
   * work: by then React has detached the node, and a detached element reports
   * `scrollTop: 0`. The first version of this flushed `panel.scrollTop` on
   * teardown and so **overwrote the correct 2,400 with 0** — the stored blob came
   * back from the deployed site as `{"?cities=san-jose": 0}`, which was worse
   * than not saving at all. Never read layout from a teardown path. (W13 R125b)
   */
  const lastTopRef = useRef<number | null>(null);
  const scrollKeyRef = useRef(filtersQS);
  useEffect(() => {
    scrollKeyRef.current = filtersQS;
    // A different search: nothing has been observed for it yet, so the unmount
    // flush must not carry the previous search's offset over to it.
    lastTopRef.current = null;
  }, [filtersQS]);
  const onListScroll = useCallback(() => {
    const panel = listPanelRef.current;
    if (!panel) return;
    lastTopRef.current = panel.scrollTop;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    const top = panel.scrollTop;
    // sessionStorage writes are synchronous; a scroll event fires dozens of
    // times a second.
    saveTimerRef.current = setTimeout(() => saveListScroll(filtersQS, top), 150);
  }, [filtersQS]);

  // Flush the last observed position on unmount — navigating to a listing tears
  // this component down, and the throttled write may not have fired yet. No DOM
  // read here, by design (see lastTopRef); and nothing is written if the buyer
  // never scrolled, so a stale entry is never replaced by a guess.
  useEffect(
    () => () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (lastTopRef.current !== null) saveListScroll(scrollKeyRef.current, lastTopRef.current);
    },
    []
  );

  // Restore once per results key, and only once there are cards to scroll past —
  // assigning scrollTop to an empty pane clamps it to 0.
  const restoredKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const panel = listPanelRef.current;
    if (!panel || listings.length === 0) return;
    if (restoredKeyRef.current === filtersQS) return;
    restoredKeyRef.current = filtersQS;
    const saved = loadListScroll(filtersQS);
    if (saved > 0) panel.scrollTop = saved;
  }, [filtersQS, listings.length]);
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);
  const page = filters.page;

  // After a user-initiated move, offer "Search this area" — or, when the
  // user opted into "search as I move", apply the bbox directly on moveend.
  const handleMapMove = useCallback(
    (nextBbox: BoundingBox) => {
      if (!mapTouched) return;
      const next = bboxToParam(nextBbox);
      if (next === (filters.bbox ?? '')) {
        setPendingBbox(null);
        return;
      }
      if (searchAsIMove) {
        // One entry per pan would make Back useless — replace instead.
        updateFilters({ bbox: next, cities: undefined }, { replace: true });
        setPendingBbox(null);
        return;
      }
      setPendingBbox(nextBbox);
    },
    [mapTouched, filters.bbox, searchAsIMove, updateFilters]
  );

  const applyMapArea = useCallback(() => {
    if (!pendingBbox) return;
    // A drawn area replaces city scoping — keeping both would AND the filters
    // and empty the results when the user pans away from the filtered city.
    updateFilters({ bbox: bboxToParam(pendingBbox), cities: undefined });
    setPendingBbox(null);
  }, [pendingBbox, updateFilters]);

  // R6: share the current search (the URL already encodes every filter).
  const [copied, setCopied] = useState(false);
  const shareSearch = useCallback(() => {
    navigator.clipboard
      ?.writeText(window.location.href)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }, []);

  /**
   * Live result count in the tab title (R13), now only for the changes the
   * server never sees.
   *
   * Applying a filter is `pushState` — no navigation, so `generateMetadata`
   * doesn't re-run and nothing would update the tab. On a hard navigation the
   * server already shipped this exact string (`searchPageTitle` builds both), so
   * React re-asserting its metadata title over this assignment is now a no-op
   * instead of dropping the count. (W16 R151)
   */
  const cityLabel = citySlugsFromFilters(filters)
    .map((slug) => titleCaseCity(slug.replace(/-/g, ' ')))
    .join(', ');
  /**
   * Why a filtered city looks empty when it isn't.
   *
   * A San Francisco buyer sees 44 homes with nothing on the page telling them
   * this is one MLS's view of their county — which reads as "the market is
   * empty" rather than "we carry a partial view here".
   *
   * The input is the **result's own `city` facet**, not the filter, because the
   * notice is about the cards on screen. The API computes that facet over the
   * whole result set: `?cities=san-francisco,atlantis` answers
   * `{"San Francisco": 44}`, so a filter term that contributes nothing cannot
   * silence a true notice — while `?cities=san-francisco,san-jose` answers
   * `{"San Francisco": 44, "San Jose": 851}` and correctly does, since a
   * coverage notice over San Jose's 851 homes would be false about most of
   * them. The filter is the fallback when the facet names nobody.
   *
   * **An empty facet is not an absent one, and `{}` is truthy.** `if (facet)`
   * made the fallback below dead code, because a zero-result search answers
   * `facets.city = {}` — measured 2026-08-02 against staging, `POST
   * /api/v1/search` with `{"cities":["Ashland"],"per_page":1}` → `total 0,
   * facets.city {}`; likewise Cherryland, Diablo, Knightsen and Port Costa,
   * all five of them in this module's own outside-the-footprint table. Control
   * in the same run: `San Jose` → 851 and the invented `Atlantis` → 0, so a
   * zero there is a real zero and not a broken query. The result was that the
   * notice went silent on exactly the page that needs it most — a buyer who
   * filters to Ashland sees "No homes in Ashland right now" with nothing
   * saying we do not carry that county's MLS, which is the "the market is
   * empty" misreading this whole feature exists to prevent.
   *
   * So: prefer the facet whenever it names a city that contributed homes, and
   * fall back to the filter otherwise. The fallback is safe precisely where it
   * now fires — with no cards on screen, a notice cannot be false about a card,
   * and `feedGapForCities` still refuses on any name it cannot place, so
   * `?cities=san-francisco,san-jose` returning zero under a narrow price band
   * stays silent.
   */
  const resultCitySlugs = useMemo(() => {
    const facet = data?.facets?.city;
    const citiesWithHomes = facet
      ? Object.entries(facet)
          .filter(([, n]) => n > 0)
          .map(([name]) => slugify(name))
      : [];
    if (citiesWithHomes.length > 0) return citiesWithHomes;
    return citySlugsFromFilters(filters);
  }, [data, filters]);
  const feedGapNote = feedGapForCities(resultCitySlugs);
  const titleBase = searchPageTitle(filters, data ? total : undefined);
  useEffect(() => {
    if (!data) return;
    document.title = `${titleBase} | GoReal`;
  }, [data, titleBase]);

  // Record the primary location/query of this search so a focused, empty
  // SearchBar can offer to resume it — however the buyer arrived (SearchBar,
  // a city card, a deep link). Dedupe-by-href in the store keeps it tidy;
  // the bare all-listings view (no city/zip/query) is intentionally skipped.
  const recentCitySlug = citySlugsFromFilters(filters)[0];
  const recentZip = filters.zips?.split(',')[0];
  const recentQ = filters.q;
  // The band travels with the place (not in the href — see lib/recentSearches):
  // for a visitor with no saved search it is the only budget signal we have, and
  // the home feed's "picked for you" rail is built from it.
  const recentPriceMax = filters.priceMax;
  const recentBedsMin = filters.bedsMin;
  useEffect(() => {
    const band = { priceMax: recentPriceMax, bedsMin: recentBedsMin };
    if (recentCitySlug) {
      recordSearch({
        label: titleCaseCity(recentCitySlug.replace(/-/g, ' ')),
        href: `/homes?cities=${recentCitySlug}`,
        kind: 'city',
        ...band,
      });
    } else if (recentZip) {
      recordSearch({ label: recentZip, href: `/homes?zips=${recentZip}`, kind: 'zip', ...band });
    } else if (recentQ) {
      recordSearch({
        label: recentQ,
        href: `/homes?q=${encodeURIComponent(recentQ)}`,
        kind: 'query',
      });
    }
  }, [recentCitySlug, recentZip, recentQ, recentPriceMax, recentBedsMin]);

  const showList = viewMode !== 'map';

  /**
   * Painted width of a result-card photo, so `srcset` picks the smallest source
   * that stays sharp. Derived from the panel width (`lg:w-[46%]` in split, full
   * in list) and the column count (`grid-cols-1 sm:grid-cols-2 lg:grid-cols-1
   * xl:grid-cols-2`), minus `p-4` + `gap-4`. Split view at 1280 works out to a
   * **270 px** box — the default `33vw` claimed 422 px and pulled a `w=640`
   * source for it.
   */
  const cardPhotoSizes =
    viewMode === 'split'
      ? '(max-width: 639px) 92vw, (max-width: 1023px) 46vw, (max-width: 1279px) 43vw, 22vw'
      : '(max-width: 639px) 92vw, (max-width: 1023px) 46vw, (max-width: 1279px) 94vw, 47vw';

  // When a search comes up empty, is a narrowing filter (not the location) the
  // likely cause? Blaming "coverage there is thin" for a city that actually has
  // thousands of homes (e.g. San Jose + Multi-Family = 0) misleads the buyer —
  // the honest fix is to widen/clear the filters, not abandon the city.
  // Filters that cannot match anything by arithmetic (min above max). A shared
  // URL is how they arrive — the schema accepts each bound on its own.
  const contradictions = rangeContradictions(filters);
  const contradictionDetail =
    contradictions[0]?.key === 'price'
      ? `You asked for ${formatPrice(filters.priceMin!)} to ${formatPrice(filters.priceMax!)}.`
      : contradictions[0]?.key === 'sqft'
        ? `You asked for ${filters.sqftMin!.toLocaleString()} to ${filters.sqftMax!.toLocaleString()} sqft.`
        : '';

  const hasNarrowingFilters =
    filters.priceMin !== undefined ||
    filters.priceMax !== undefined ||
    filters.bedsMin !== undefined ||
    filters.bathsMin !== undefined ||
    filters.sqftMin !== undefined ||
    filters.sqftMax !== undefined ||
    filters.yearMin !== undefined ||
    !!filters.oh ||
    !!filters.cut ||
    filters.hoaMax !== undefined ||
    filters.domMax !== undefined ||
    !!filters.zips ||
    propertyTypesFromFilters(filters).length > 0;

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col">
      {/*
        The page's heading. This page had NO h1 — and no heading of any level
        outside its error and empty states — while being one of the two pages
        the whole site links into. A screen-reader user jumping by heading
        landed on a search results page with nothing announcing what it was,
        and the largest indexable page on the site shipped without one.

        Visually hidden, because the design's answer to "what is this page" is
        the filter bar and the result count beside it, and a slab of display
        type above a full-height map/list split would be a heading for the
        crawler at the buyer's expense. Hidden from sight is not hidden from
        the accessibility tree — `sr-only` keeps it in both the heading outline
        and the a11y tree.

        Built from `searchPageTitle`, the same function that produces the tab
        title and the server's `generateMetadata`, so the page cannot announce
        one thing to a crawler and another to a screen reader. Restating the
        string here is how those two drift.
      */}
      <h1 className="sr-only">{titleBase}</h1>

      {/* Search + filters bar */}
      <div className="space-y-3 border-b border-hairline bg-canvas/95 p-4">
        <div className="max-w-xl">
          <SearchBar size="md" placeholder="City, ZIP, address, or MLS#…" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <FilterPanel
            filters={filters}
            total={data ? total : undefined}
            onChange={updateFilters}
            onReset={resetFilters}
            facets={data?.facets}
          />

          {/* Share current search */}
          <button
            onClick={shareSearch}
            className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium text-ink-soft transition-colors hover:bg-glass hover:text-ink"
            title="Copy a link to this search"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 text-emerald-400" /> Copied
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4" /> Share
              </>
            )}
          </button>

          {/* View toggle */}
          <div className="glass ml-auto flex items-center gap-1 rounded-xl p-1">
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'rounded-lg p-2 transition-colors',
                viewMode === 'list' ? 'bg-emerald-500/15 text-emerald-300' : 'text-ink-muted hover:text-ink'
              )}
              title="List view"
              aria-pressed={viewMode === 'list'}
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('split')}
              className={cn(
                'hidden rounded-lg p-2 transition-colors lg:block',
                viewMode === 'split' ? 'bg-emerald-500/15 text-emerald-300' : 'text-ink-muted hover:text-ink'
              )}
              title="Split view"
              aria-pressed={viewMode === 'split'}
            >
              <Columns2 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={cn(
                'rounded-lg p-2 transition-colors',
                viewMode === 'map' ? 'bg-emerald-500/15 text-emerald-300' : 'text-ink-muted hover:text-ink'
              )}
              title="Map view"
              aria-pressed={viewMode === 'map'}
            >
              <MapIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Active filter chips */}
        {(() => {
          const chips: { label: string; clear: Partial<SearchFilters> }[] = [];
          if (filters.q) chips.push({ label: `“${filters.q}”`, clear: { q: undefined } });
          for (const slug of citySlugsFromFilters(filters)) {
            chips.push({
              label: titleCaseCity(slug.replace(/-/g, ' ')),
              clear: {
                cities:
                  citySlugsFromFilters(filters).filter((c) => c !== slug).join(',') || undefined,
              },
            });
          }
          if (filters.priceMin !== undefined || filters.priceMax !== undefined) {
            const lo = filters.priceMin !== undefined ? formatPrice(filters.priceMin) : '';
            const hi = filters.priceMax !== undefined ? formatPrice(filters.priceMax) : '';
            chips.push({
              label: lo && hi ? `${lo}–${hi}` : lo ? `${lo}+` : `<${hi}`,
              clear: { priceMin: undefined, priceMax: undefined },
            });
          }
          if (filters.bedsMin !== undefined)
            chips.push({ label: `${filters.bedsMin}+ bd`, clear: { bedsMin: undefined } });
          if (filters.bathsMin !== undefined)
            chips.push({ label: `${filters.bathsMin}+ ba`, clear: { bathsMin: undefined } });
          const sqft = sqftLabel(filters);
          if (sqft) chips.push({ label: sqft, clear: { sqftMin: undefined, sqftMax: undefined } });
          if (filters.yearMin !== undefined)
            chips.push({ label: `Built ${filters.yearMin}+`, clear: { yearMin: undefined } });
          if (filters.oh) chips.push({ label: 'Open house', clear: { oh: undefined } });
          if (filters.cut) chips.push({ label: 'Price cut', clear: { cut: undefined } });
          if (filters.hoaMax !== undefined)
            chips.push({
              label: filters.hoaMax === 0 ? 'No HOA' : `HOA ≤ $${filters.hoaMax}`,
              clear: { hoaMax: undefined },
            });
          if (filters.domMax !== undefined)
            chips.push({ label: `Listed ≤ ${filters.domMax}d`, clear: { domMax: undefined } });
          for (const zip of filters.zips ? filters.zips.split(',') : []) {
            chips.push({
              label: `ZIP ${zip}`,
              clear: {
                zips:
                  filters
                    .zips!.split(',')
                    .filter((z) => z !== zip)
                    .join(',') || undefined,
              },
            });
          }
          // Only when the box actually parsed. A junk or degenerate bbox
          // (`999,999,999,999`, or min == max) is dropped by `bboxFromFilters`,
          // so the chip used to advertise a map filter that was not in
          // effect — 8,587 homes next to a "Map area" pill. (W12 R117)
          if (filters.bbox && appliedBbox)
            chips.push({ label: 'Map area', clear: { bbox: undefined } });
          for (const t of propertyTypesFromFilters(filters)) {
            chips.push({
              label: getPropertyTypeLabel(t),
              clear: {
                types:
                  propertyTypesFromFilters(filters).filter((x) => x !== t).join(',') || undefined,
              },
            });
          }
          if (chips.length === 0) return null;
          return (
            <div className="flex flex-wrap items-center gap-2">
              {chips.map((chip) => (
                <button
                  key={chip.label}
                  onClick={() => updateFilters(chip.clear)}
                  className="group flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 py-1 pl-3 pr-2 text-xs font-medium text-emerald-300 transition-colors hover:bg-emerald-500/20"
                  title={`Remove ${chip.label}`}
                >
                  {chip.label}
                  <X className="h-3 w-3" />
                </button>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Listings panel */}
        <div
          ref={listPanelRef}
          onScroll={onListScroll}
          className={cn(
            'custom-scrollbar overflow-y-auto bg-canvas',
            !showList && 'hidden',
            viewMode === 'split' && 'w-full lg:w-[46%] lg:flex-none',
            viewMode === 'list' && 'w-full'
          )}
        >
          {/* Subtle top progress bar while refetching over existing results
              (keepPreviousData keeps the old list visible). */}
          {isFetching && listings.length > 0 && (
            <div
              aria-hidden
              className="sticky inset-x-0 top-0 z-10 h-0.5 animate-pulse bg-emerald-500"
            />
          )}
          {isFetching && listings.length === 0 ? (
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="space-y-3">
                  <Skeleton className="aspect-[4/3] w-full" />
                  <Skeleton className="h-5 w-2/5" />
                  <Skeleton className="h-4 w-4/5" />
                </div>
              ))}
            </div>
          ) : error && listings.length === 0 ? (
            // Two mobile defects, both screenshot-confirmed at 390 (W12 R118):
            // `h-full` + `justify-center` on content taller than the panel
            // clipped the heading off the top with nothing to scroll — the
            // multi-family empty state literally read "Jose". `min-h-full` lets
            // the panel scroll instead. And the fixed Map toggle (bottom-left)
            // plus "Talk to a human" (bottom-right) sat on top of the recovery
            // buttons, so `pb-28` keeps them clear.
            <div className="flex min-h-full flex-col items-center justify-center p-8 pb-28 text-center sm:pb-8">
              <SearchX className="mb-4 h-14 w-14 text-ink-faint" />
              <h2 className="mb-2 text-lg font-semibold text-ink">Search is unavailable</h2>
              <p className="max-w-sm text-ink-muted">
                We couldn&apos;t reach the search service. It&apos;s usually temporary
                {CONTACT_PHONE && CONTACT_PHONE_TEL ? (
                  <>
                    {' '}
                    — or call us at{' '}
                    <a href={CONTACT_PHONE_TEL} className="text-emerald-300 hover:text-emerald-700">
                      {CONTACT_PHONE}
                    </a>
                  </>
                ) : (
                  <>
                    {' '}
                    — or{' '}
                    <a href={CONTACT_EMAIL_HREF} className="text-emerald-300 hover:text-emerald-700">
                      email us
                    </a>
                  </>
                )}
                .
              </p>
              <Button
                size="sm"
                className="mt-6"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <RotateCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
                {isFetching ? 'Retrying…' : 'Try again'}
              </Button>
            </div>
          ) : listings.length === 0 && total > 0 ? (
            // A page past the end of the results. Without this branch the
            // generic empty state fired and claimed "No homes in Palo Alto
            // right now — coverage there can be thin" while the header read
            // "133 homes" and the map beside it was full of pins, with no
            // pagination control left to escape with. (W12 R116)
            <div className="flex min-h-full flex-col items-center justify-center p-8 pb-28 text-center sm:pb-8">
              <SearchX className="mb-4 h-14 w-14 text-ink-faint" />
              <h2 className="mb-2 text-lg font-semibold text-ink">
                There&apos;s no page {page + 1}
              </h2>
              <p className="max-w-sm text-ink-muted">
                This search has {total.toLocaleString()} home{total === 1 ? '' : 's'} across{' '}
                {totalPages.toLocaleString()} page{totalPages === 1 ? '' : 's'}
                {cityLabel ? ` in ${cityLabel}` : ''} — the filters are fine, the page number
                isn&apos;t.
              </p>
              <div className="mt-6 flex items-center gap-3">
                <Button size="sm" onClick={() => updateFilters({ page: 0 })}>
                  Go to page 1
                </Button>
                {totalPages > 1 && (
                  <button
                    onClick={() => updateFilters({ page: totalPages - 1 })}
                    className="text-sm font-medium text-emerald-300 transition-colors hover:text-emerald-700"
                  >
                    Last page ({totalPages.toLocaleString()})
                  </button>
                )}
              </div>
            </div>
          ) : listings.length === 0 ? (
            <div className="flex min-h-full flex-col items-center justify-center p-8 pb-28 text-center sm:pb-8">
              <SearchX className="mb-4 h-14 w-14 text-ink-faint" />
              <h2 className="mb-2 text-lg font-semibold text-ink">
                {contradictions.length > 0
                  ? `Your ${contradictions[0].label} runs backwards`
                  : filters.q
                    ? `No matches for “${filters.q}”`
                    : cityLabel
                      ? hasNarrowingFilters
                        ? `No homes match these filters in ${cityLabel}`
                        : `No homes in ${cityLabel} right now`
                      : 'No homes match these filters'}
              </h2>
              <p className="max-w-sm text-ink-muted">
                {contradictions.length > 0
                  ? `The minimum is above the maximum, so nothing can match. ${contradictionDetail}`
                  : filters.q
                    ? 'Check the spelling, or try a city, ZIP, or MLS number. Our feed is MLSListings — some areas (like San Francisco) are thin.'
                    : cityLabel
                      ? hasNarrowingFilters
                        ? `${cityLabel} has homes — just none matching all of these. Try clearing or widening your filters.`
                        : 'Coverage there can be thin. Try a nearby city, widen your filters, or move the map to explore.'
                      : 'Try widening your filters or moving the map to explore different areas.'}
              </p>
              {contradictions.length > 0 && (
                <Button
                  size="sm"
                  className="mt-5"
                  onClick={() => updateFilters(contradictions[0].clear)}
                >
                  Clear the {contradictions[0].label}
                </Button>
              )}

              {/* The same notice as the results branch, on the page that needs
                  it most.

                  A zero-result city search is the strongest form of the
                  misreading: "No homes in Ashland right now" with no further
                  word reads as "nobody is selling in Ashland", when the fact is
                  that we do not carry Alameda County's MLS. Measured live
                  2026-08-02 — Ashland, Cherryland, Diablo, Knightsen and Port
                  Costa all return total 0, and all five are in this module's
                  outside-the-footprint table.

                  Not shown beside a contradiction, and not shown when a
                  NARROWING FILTER is applied: in both cases the cause of the
                  zero is the buyer's own query, and a coverage sentence would
                  misattribute it. "San Francisco + 5 beds minimum = 0" is not
                  evidence about the footprint, and telling that buyer their
                  county is outside it sends them away from a search they could
                  have widened. This is the same rule the two branches below
                  already apply when choosing between "clear your filters" and
                  "coverage here is thin" — the coverage note has to obey it
                  too, or the fix reintroduces the misattribution it removed. */}
              {contradictions.length === 0 && !hasNarrowingFilters && feedGapNote && (
                <p className="mt-6 max-w-sm rounded-lg border border-hairline bg-paper-raised px-4 py-3 text-left text-sm text-ink-muted">
                  {feedGapNote}
                </p>
              )}

              {/* Discovery: jump straight into a well-covered city. */}
              <div className="mt-6 w-full max-w-sm">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-faint">
                  Browse a popular area
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {DISCOVERY_CITIES.filter(
                    (c) => !citySlugsFromFilters(filters).includes(c.slug)
                  )
                    .slice(0, 5)
                    .map((c) => (
                      <button
                        key={c.slug}
                        onClick={() => applyFilters(clearedTo({ cities: c.slug }))}
                        className="flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-sm font-medium text-emerald-300 transition-colors hover:bg-emerald-500/20"
                      >
                        <MapPin className="h-3.5 w-3.5" />
                        {c.name}
                      </button>
                    ))}
                </div>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <Button variant="secondary" size="sm" onClick={resetFilters}>
                  Clear filters
                </Button>
                <button
                  onClick={() => applyFilters(clearedTo())}
                  className="text-sm font-medium text-emerald-300 transition-colors hover:text-emerald-700"
                >
                  All Bay Area homes
                </button>
              </div>
            </div>
          ) : (
            <div className="p-4">
              {/* Quiet and factual, above the cards it is about. Not an
                  apology banner: the feed's footprint is a fact the buyer needs
                  in order to read the count correctly. */}
              {feedGapNote && (
                <p className="mb-4 rounded-lg border border-hairline bg-paper-raised px-4 py-3 text-sm text-ink-muted">
                  {feedGapNote}
                </p>
              )}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {listings.map((listing, i) => (
                  <ListingCard
                    key={listing.id}
                    listing={listing}
                    priority={i < 2}
                    photoSizes={cardPhotoSizes}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <nav className="mt-6 flex items-center justify-center gap-2" aria-label="Pagination">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => updateFilters({ page: Math.max(0, page - 1) })}
                    disabled={page === 0 || isFetching}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Prev
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) pageNum = i;
                      else if (page < 3) pageNum = i;
                      else if (page >= totalPages - 3) pageNum = totalPages - 5 + i;
                      else pageNum = page - 2 + i;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => updateFilters({ page: pageNum })}
                          disabled={isFetching}
                          aria-current={pageNum === page ? 'page' : undefined}
                          className={cn(
                            'h-8 w-8 rounded-lg text-sm font-medium tabular-nums transition-colors',
                            pageNum === page
                              ? 'bg-emerald-500 text-canvas'
                              : 'glass text-ink-soft hover:bg-glass-bright'
                          )}
                        >
                          {pageNum + 1}
                        </button>
                      );
                    })}
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => updateFilters({ page: page + 1 })}
                    disabled={page >= totalPages - 1 || isFetching}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </nav>
              )}
            </div>
          )}
        </div>

        {/* Map panel */}
        {mapMounted && (
        <div
          className={cn(
            'relative',
            viewMode === 'split' && 'flex-1',
            viewMode === 'map' && 'block w-full'
          )}
          onPointerDown={() => setMapTouched(true)}
          onWheel={() => setMapTouched(true)}
        >
          <SearchMap
            listings={mapListings}
            matchCount={total}
            fitKey={filtersQS || 'all'}
            autoFit={!mapTouched}
            initialBounds={initialBounds}
            onMove={handleMapMove}
            className="h-full w-full"
          />

          <label className="glass glass-blur absolute left-3 top-3 z-10 flex cursor-pointer items-center gap-2 rounded-full bg-raised/90 px-3 py-1.5 text-xs font-medium text-ink-soft">
            <input
              type="checkbox"
              checked={searchAsIMove}
              onChange={(e) => setSearchAsIMove(e.target.checked)}
              className="h-3.5 w-3.5 accent-emerald-500"
            />
            Search as I move
          </label>

          {pendingBbox && !searchAsIMove && (
            <div className="absolute left-1/2 top-4 z-10 -translate-x-1/2">
              <button
                onClick={applyMapArea}
                className="flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-canvas shadow-lift transition-colors hover:bg-emerald-600"
              >
                <Search className="h-4 w-4" />
                Search this area
              </button>
            </div>
          )}

          {/* Map-only / mobile has no list beside it to explain an empty result —
              give the blank map its own guidance + a way out (split shows the
              list's empty state instead, so this is scoped to the map view). */}
          {viewMode === 'map' && data && total === 0 && !isFetching && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center p-6">
              <div className="glass-blur pointer-events-auto max-w-xs rounded-card border border-hairline-strong bg-raised/95 p-5 text-center shadow-lift-lg">
                <SearchX className="mx-auto mb-2 h-8 w-8 text-ink-faint" />
                <p className="mb-1 font-semibold text-ink">No homes match here</p>
                <p className="mb-3 text-sm text-ink-muted">
                  Widen your filters or move the map to explore a different area.
                </p>
                <Button variant="secondary" size="sm" onClick={resetFilters}>
                  Clear filters
                </Button>
              </div>
            </div>
          )}

          {isFetching && (
            <div
              className={cn(
                'pointer-events-none absolute left-1/2 z-10 -translate-x-1/2',
                pendingBbox ? 'top-16' : 'top-4'
              )}
            >
              <div className="glass glass-blur flex items-center gap-2 rounded-full bg-raised/90 px-4 py-2 shadow-lift">
                <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
                <span className="text-sm text-ink-soft">Searching…</span>
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Mobile map/list floating toggle — bottom-LEFT so it never collides
          with the bottom-right "Talk to a human" float (both are fixed). */}
      <div className="fixed bottom-6 left-4 z-40 lg:hidden">
        <button
          onClick={() => setViewMode(viewMode === 'map' ? 'list' : 'map')}
          className="glass-blur flex items-center gap-2 rounded-full border border-hairline-strong bg-raised/95 px-5 py-3 text-sm font-semibold text-ink shadow-lift-lg"
        >
          {viewMode === 'map' ? (
            <>
              <List className="h-4 w-4 text-emerald-300" /> List
            </>
          ) : (
            <>
              <MapIcon className="h-4 w-4 text-emerald-300" /> Map
            </>
          )}
        </button>
      </div>
    </div>
  );
}
